/**
 * The builder's column rule — which measurement columns the TRAINER sees for one exercise.
 *
 * Ported verbatim from `move-with-me-frontend/.../shared/measurement/builderColumns.ts`, minus the
 * logger half of that folder, which this app has no runtime for.
 *
 * **Values are the state.** A column carrying a value in any cycle of the plan is shown; removing a
 * column means clearing its values and nothing more, so the removal survives to the next editing
 * session without a byte being stored to say so. `revealed` is the one thing values cannot express:
 * a column the trainer opened that has nothing behind it yet.
 *
 * Everything is intersected with what the exercise MEASURES — the contract's ceiling — so an
 * exercise that measures nothing yields no columns and nothing addable, with no special case.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import { defaultVisibleFields } from './fieldDictionary';
import type { MeasurementFieldKey } from './measurementField';
import { hasValue, renderableColumns, type MeasurementValues } from './measurementValues';

export interface BuilderColumnsInput {
  /** The exercise's measurement contract — the ceiling on everything below. */
  readonly measured: readonly MeasurementFieldKey[];
  /**
   * Every prescription this exercise carries across EVERY cycle: its authored base sets plus each
   * per-cycle manual override. "Carries a value" means present — zero is a legitimate prescription.
   */
  readonly prescribed: readonly MeasurementValues[];
  /**
   * The fields the trainer has on screen with nothing behind them yet — the dictionary's starting
   * point on a freshly inserted exercise, plus any column they reopened after clearing it.
   *
   * Editing-session scope only: nothing emits it and no load reconstructs it, which is exactly why
   * a removal persists across sessions.
   */
  readonly revealed?: readonly MeasurementFieldKey[];
}

/**
 * The columns the builder shows:
 *
 *   (carries a value in any cycle ∪ revealed this editing session) ∩ measured
 *
 * in the dictionary's order.
 */
export function planBuilderColumns({
  measured,
  prescribed,
  revealed = [],
}: BuilderColumnsInput): MeasurementFieldKey[] {
  const measuredSet = new Set(measured);
  const visible = new Set<MeasurementFieldKey>();

  for (const field of revealed) {
    if (measuredSet.has(field)) visible.add(field);
  }
  for (const values of prescribed) {
    for (const field of measured) {
      if (visible.has(field)) continue;
      if (hasValue(values, field)) visible.add(field);
    }
  }

  return renderableColumns(visible);
}

/**
 * Where a newly inserted exercise starts: default-visible ∩ measured. Every one of them is
 * removable — removing says "I am not prescribing this" and nothing more.
 */
export function builderStartingColumns(
  measured: readonly MeasurementFieldKey[],
): MeasurementFieldKey[] {
  const measuredSet = new Set(measured);
  return renderableColumns(defaultVisibleFields().filter((field) => measuredSet.has(field)));
}

/**
 * What is left to OPEN: measured, minus what is already on screen, minus what the wire cannot carry.
 *
 * The inverse of `planBuilderColumns` against the same ceiling, and the expression that makes
 * removing a column reversible — a column cleared away has to be offerable again, or the removal is
 * a one-way door on data the trainer may have meant to keep.
 *
 * The wire-column condition is the same promise from the other side: tempo and speed are measured,
 * addable-looking fields with no numeric cell on the set, so offering them would open a column
 * whose value goes nowhere. Ported from mobile's `addableFields`, which is the one gate both of
 * that app's surfaces use.
 */
export function addableFields(
  measured: readonly MeasurementFieldKey[],
  visible: readonly MeasurementFieldKey[],
): MeasurementFieldKey[] {
  const shown = new Set(visible);
  return renderableColumns(measured.filter((field) => !shown.has(field)));
}
